import { Type } from '@angular/core';

import _ from 'underscore';

import { FilterTypesEnum } from '../../../Common/Filter/enums/filters-types.enum';
import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { CustomFilterTypeModel } from '../../../Common/Filter/models/custom-filter-type.model';
import { AbstractFilterTypeComponent } from '../../../Common/Filter/models/abstract-filter-type.component';
import { operatorsEnum } from '../enums/operators.enum';
import { MonitoringKeywordGroupModel } from './monitoring-keyword-group.model';
import { MonitoringAdvancedBooleanModel } from './monitoring-advanced-boolean.model';
import { FilterQuickOptionModel } from '../../../Common/Filter/models/filter-quick-option.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { SearchContextModel } from './search-context.model';
import { SearchContextsEnum } from '../enums/search-contexts.enum';

// item order matters for _.isEqual() comparison
export const allSearchContexts = [ SearchContextsEnum.header, SearchContextsEnum.lead, SearchContextsEnum.body ];

export enum KeywordFilterModeEnum {
    standard,
    expert
}

export interface MonitoringKeywordFilterTypeModel {
    keywordGroups: MonitoringKeywordGroupModel[];
    expertQuery: MonitoringAdvancedBooleanModel;
}

export class MonitoringKeywordFilterModel extends CustomFilterTypeModel<MonitoringKeywordFilterTypeModel> {

    readonly key: FilterTypesEnum = FilterTypesEnum.custom;

    protected value: MonitoringKeywordFilterTypeModel;
    protected currentValue: MonitoringKeywordFilterTypeModel;

    filterMode: KeywordFilterModeEnum = KeywordFilterModeEnum.standard;
    errors: string[] = []; // array of error messages

    constructor(
        id: string,
        label: string,
        component: Type<AbstractFilterTypeComponent>,
        private localizationService: LocalizationService,
        section?: string,
        useExactQuickSearchString?: boolean
    ) {
        super(id, label, component, section);

        this.useExactQuickSearchString = useExactQuickSearchString;

        this.setValue({
            keywordGroups: [ new MonitoringKeywordGroupModel() ],
            expertQuery: new MonitoringAdvancedBooleanModel(),
        });
    }

    /**
     * Sets the active value for a filter
     * @param value
     */
    setValue(value: MonitoringKeywordFilterTypeModel): void {
        this.value = value;
        this.chips = this.makeChips(value);
        this.cancelChanges();
    }

    /**
     * Gets the active value for a filter
     */
    getValue(): MonitoringKeywordFilterTypeModel {
        return this.value;
    }

    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    removeChip(chip: ChipModel): void {
        if (this.filterMode === KeywordFilterModeEnum.standard) {
            const keywordGroupIndex = chip.labelArgs['groupId'];
            if (keywordGroupIndex > -1) {
                const keywordGroup = this.value.keywordGroups[keywordGroupIndex];
                switch (chip.labelArgs['operator']) {
                    case operatorsEnum.and:
                        keywordGroup.and = keywordGroup.and.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                    case operatorsEnum.or:
                        keywordGroup.or = keywordGroup.or.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                    case operatorsEnum.not:
                        keywordGroup.not = keywordGroup.not.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                }
                if (!keywordGroup.hasAnyKeywords()) {
                    this.value.keywordGroups.splice(keywordGroupIndex, 1);
                }
            }
        } else {
            this.value.expertQuery.filter = '';
        }

        this.setValue(this.value);
    }

    /**
     * Resets the filter to not apply any changes
     */
    clear(): void {
        this.setValue({
            keywordGroups: [ new MonitoringKeywordGroupModel() ],
            expertQuery: new MonitoringAdvancedBooleanModel(),
        });
    }

    /**
     * Sets the current value for a filter
     * @param value
     */
    setCurrentValue(value: MonitoringKeywordFilterTypeModel): void {
        this.currentValue = value;
        this.currentChips = this.makeChips(this.currentValue);
    }

    /**
     * Gets the current value for a filter
     */
    getCurrentValue(): MonitoringKeywordFilterTypeModel {
        return this.currentValue;
    }

    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    removeCurrentChip(chip: ChipModel): void {
        if (this.filterMode === KeywordFilterModeEnum.standard) {
            const keywordGroupIndex = chip.labelArgs['groupId'];
            if (keywordGroupIndex > -1) {
                const keywordGroup = this.currentValue.keywordGroups[keywordGroupIndex];
                switch (chip.labelArgs['operator']) {
                    case operatorsEnum.and:
                        keywordGroup.and = keywordGroup.and.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                    case operatorsEnum.or:
                        keywordGroup.or = keywordGroup.or.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                    case operatorsEnum.not:
                        keywordGroup.not = keywordGroup.not.filter(x => x !== chip.labelArgs['keyword']);
                        break;
                }
                if (!keywordGroup.hasAnyKeywords()) {
                    this.currentValue.keywordGroups.splice(keywordGroupIndex, 1);
                }
            }
        } else {
            this.currentValue.expertQuery.filter = '';
        }

        this.setCurrentValue(this.currentValue);
    }

    /**
     * Resets the current value to the active value
     */
    cancelChanges(): void {
        this.setCurrentValue(this.cloneValue(this.value));
    }

    private makeChips(value: MonitoringKeywordFilterTypeModel): ChipModel[] {
        const chips: ChipModel[] = [];
        if (value) {
            if (this.filterMode === KeywordFilterModeEnum.standard) {
                // If there are more than one keyword groups, chips should be displayed in groups
                const hasMoreThanOneGroup = value.keywordGroups.filter(x => x.isAnyKeywordSet())?.length > 1;
                value.keywordGroups.forEach((keywordGroup, index) => {
                    let searchArea = '';
                    const names = keywordGroup.searchContexts.map(x => this.localizationService.transform(x.name));
                    if (names.length > 0 && names.length < 3) {
                        searchArea = ', ' + names.reduce((str, current) => str + `, ${current}`);
                    }
                    const chipLength = chips.length;
                    keywordGroup.or.forEach(filter => {
                        chips.push({
                            label: filter + ' ' + `(${operatorsEnum.or}${searchArea})`,
                            labelArgs: {
                                groupId: index,
                                operator: operatorsEnum.or,
                                keyword: filter,
                                searchContexts: keywordGroup.searchContexts
                            }
                        });
                    });
                    keywordGroup.and.forEach(filter => {
                        chips.push({
                            label: filter + ' ' + `(${operatorsEnum.and}${searchArea})`,
                            labelArgs: {
                                groupId: index,
                                operator: operatorsEnum.and,
                                keyword: filter,
                                searchContexts: keywordGroup.searchContexts
                            }
                        });
                    });
                    keywordGroup.not.forEach(filter => {
                        chips.push({
                            label: filter + ' ' + `(${operatorsEnum.not}${searchArea})`,
                            labelArgs: {
                                groupId: index,
                                operator: operatorsEnum.not,
                                keyword: filter,
                                searchContexts: keywordGroup.searchContexts
                            }
                        });
                    });
                    if(hasMoreThanOneGroup && chips.length) {
                        chips[chipLength].labelArgs['firstInGroup'] = true;
                        chips[chips.length - 1].labelArgs['lastInGroup'] = true;
                    }
                });
                if (hasMoreThanOneGroup && chips.length) {
                    chips[0].labelArgs['firstInFilter'] = true;
                    chips[chips.length - 1].labelArgs['lastInFilter'] = true;
                }
            } else {
                if (value.expertQuery.filter !== '') {
                    chips.push({
                        label: 'Expert Query',
                        tooltip: value.expertQuery.filter,
                        labelArgs: { searchContexts: value.expertQuery.searchContexts.map(x => x.value) }
                    });
                }
            }
        }
        return chips;
    }

    getFilterMode(): KeywordFilterModeEnum {
        return this.filterMode;
    };

    setFilterMode(mode: KeywordFilterModeEnum) {
        this.filterMode = mode;
    };

    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel<string>[] {
        // block quick autocomplete when expert query is applied
        if (this.value.expertQuery.filter !== '') {
            return;
        }
        const quickKeywordGroup = this.value.keywordGroups?.find(
            x => _.isEqual(x.searchContexts.map(y => y.value), allSearchContexts)
        );

        if (quickKeywordGroup && !quickKeywordGroup.canAddCurrentItem(query, operatorsEnum.and)) {
            return [];
        }

        return [{
            label: query,
            value: query,
            filter: this
        }];
    }

    addQuickOption(option: FilterQuickOptionModel<string>): void {
        let keywordGroupIndex = this.value.keywordGroups.findIndex(x => {
            const sortedItemSearchContexts = x.searchContexts.map(y => y.value).sort();
            const sortedAllSearchContexts = [...allSearchContexts].sort();
            return _.isEqual(sortedItemSearchContexts, sortedAllSearchContexts);
        });
        if (keywordGroupIndex === -1) {
            const newKeywordGroup = new MonitoringKeywordGroupModel();
            newKeywordGroup.searchContexts = allSearchContexts.map(x => new SearchContextModel(x));
            this.value.keywordGroups.push(newKeywordGroup);
            keywordGroupIndex = this.value.keywordGroups.length - 1;
        }
        if(!this.value.keywordGroups[keywordGroupIndex].and.includes(option.value)) {
            this.value.keywordGroups[keywordGroupIndex].and.push(option.value);
        }
        this.filterMode = KeywordFilterModeEnum.standard;
        this.setValue(this.value);
    }

    cloneValue(model: MonitoringKeywordFilterTypeModel): MonitoringKeywordFilterTypeModel {
        const keywordGroups = model.keywordGroups.map(x => x.clone());
        const expertQuery = model.expertQuery.clone();

        return {
            keywordGroups: keywordGroups,
            expertQuery: expertQuery,
        };
    }

    getKeywordGroupsForSearchQuery(): MonitoringKeywordGroupModel[] {
        const res = [];
        this.getCurrentValue().keywordGroups.forEach(keywordGroup => {
            const newKeywordGroup = new MonitoringKeywordGroupModel();
            newKeywordGroup.searchContexts = keywordGroup.searchContexts;
            newKeywordGroup.and = keywordGroup.and;
            newKeywordGroup.or = keywordGroup.or;
            newKeywordGroup.not = keywordGroup.not;
            newKeywordGroup.searchContexts = [ ...keywordGroup.searchContexts ];

            res.push(newKeywordGroup);
        });
        return res;
    }

    getErrorMessages(showAsList?: boolean, separator?: string): string {
        if (!this.errors?.length) {
            return '';
        }

        return showAsList
            ? this.errors.map(error => `${separator ?? '•'} ${error}`).join('\n')
            : this.errors.join(separator ?? ', ');
    }
}
